import ReactECharts from 'echarts-for-react';

/**
 * RaisyStackChart_3 — "dark command-deck" variant of RaisyStackChart (blog01).
 *
 * Same data, same chart family (sunburst — hierarchical part-to-whole).
 * Opaque slate-900 background, light text, teal/cyan accents, subtle dark
 * segment separators. Confident dashboard feel.
 */

const PALETTE = {
  teal: '#0AB3BF', // Entity accent
  cyan: '#4DD4E0', // Bot accent
  tealDeep: '#0E7490', // Process (cyan-700, reads on dark)
  panel: '#1E293B', // Kernel segments (slate-800)
  surface: '#0F172A', // opaque slate-900 card
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  onAccent: '#0F172A', // dark text on bright teal/cyan fills
  border: '#334155', // subtle slate-700 separators
};

interface StackNode {
  name: string;
  value?: number;
  itemStyle?: { color: string };
  children?: StackNode[];
}

const stackData: StackNode[] = [
  {
    name: 'Entity',
    itemStyle: { color: PALETTE.teal },
    children: [
      {
        name: 'Process',
        itemStyle: { color: PALETTE.tealDeep },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: PALETTE.cyan },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
            ],
          },
          {
            name: 'Bot',
            itemStyle: { color: PALETTE.cyan },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
            ],
          },
        ],
      },
      {
        name: 'Process',
        itemStyle: { color: PALETTE.tealDeep },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: PALETTE.cyan },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.panel } },
            ],
          },
        ],
      },
    ],
  },
];

/** ONE shared level-name + description source used by label and tooltip. */
const levelMeta: Record<string, string> = {
  Entity: 'Who acts — companies, products, people',
  Process: 'What they do repeatedly — workflows in a domain',
  Bot: 'Who executes each step — bots or humans',
  Kernel: 'What the actor knows — rules, context, schema',
};

const labelOf = (name: string) => name;

const tooltipOf = (name: string) =>
  `${name}<br/><span style="color:#94A3B8">${levelMeta[name] ?? 'Raisy building block'}</span>`;

export const RaisyStackChart_3 = ({ height = 380 }: { height?: number }) => {
  const option = {
    backgroundColor: PALETTE.surface,
    tooltip: {
      trigger: 'item',
      backgroundColor: PALETTE.panel,
      borderColor: PALETTE.border,
      textStyle: { color: PALETTE.textPrimary, fontSize: 13 },
      formatter: (p: { name: string }) => tooltipOf(p.name),
    },
    series: [
      {
        type: 'sunburst',
        data: stackData,
        radius: [0, '92%'],
        center: ['50%', '52%'],
        sort: undefined,
        emphasis: { focus: 'ancestor' },
        label: {
          rotate: 0,
          color: PALETTE.textPrimary,
          fontSize: 13,
          fontWeight: 700,
          formatter: (p: { name: string }) => labelOf(p.name),
        },
        itemStyle: { borderColor: PALETTE.surface, borderWidth: 2 },
        levels: [
          {},
          { r0: '0%', r: '34%', label: { fontSize: 16, color: PALETTE.onAccent, fontWeight: 800 } },
          { r0: '34%', r: '62%', label: { fontSize: 13, color: PALETTE.textPrimary, fontWeight: 700 } },
          { r0: '62%', r: '82%', label: { fontSize: 12, color: PALETTE.onAccent, fontWeight: 700 } },
          { r0: '82%', r: '100%', label: { fontSize: 10, color: PALETTE.textSecondary, fontWeight: 600 } },
        ],
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-400 text-center">
        Figure 2. The Raisy stack, top-down: an Entity owns Processes, each Process is executed by Bots, and each Bot
        loads Kernels — the rules it knows. Click a segment to zoom; hover for the level definition.
      </p>
    </div>
  );
};
